import React from 'react';
import { donateEvent } from '@kuow/core';

const VISIBLE_SHARE = 0.5;
// Intermediate steps re-run the callback as a tall element scrolls in, so the
// "fills half the viewport" rule can trigger before the ratio reaches 0.5.
const THRESHOLDS = Array.from({ length: 11 }, (_, i) => i / 20);

const isSeen = (entry: IntersectionObserverEntry): boolean => {
  if (!entry.isIntersecting) return false;
  if (entry.intersectionRatio >= VISIBLE_SHARE) return true;
  const viewportHeight = entry.rootBounds?.height ?? window.innerHeight;
  return entry.intersectionRect.height >= viewportHeight * VISIBLE_SHARE;
};

/**
 * Fires one `donate` / `Impression` event the first time the element is at least
 * half visible (or fills half the viewport), then stops observing. Rough volume
 * signal only: no dwell time, tab-visibility, or render-readiness checks.
 */
export function useDonateImpression(
  ref: React.RefObject<Element | null>,
  placement: string,
  enabled = true,
): void {
  React.useEffect(() => {
    const element = ref.current;
    if (!enabled || !element || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some(isSeen)) return;
        observer.disconnect();
        donateEvent('Impression', placement);
      },
      { threshold: THRESHOLDS },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, placement, enabled]);
}
